import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BarChart3,
  Blocks,
  LayoutDashboard,
  Plus,
  Settings,
  type LucideIcon,
} from 'lucide-react'
import {
  isMobileModuleNavTabActive,
  MOBILE_MODULE_EXIT,
  resolveMobileModuleNav,
  type MobileModuleNavTab,
} from '@/constants/mobile-module-nav'
import { ROUTES } from '@/constants/routes'
import { salesLeadsListHref, type LeadListTab } from '@/modules/sales/utils/lead-list-params'
import { useAuth } from '@/hooks/useAuth'
import { usePrefersReducedMotion } from '@/hooks/useMediaQuery'
import { cn } from '@/utils/cn'

type GlobalTabId = 'dashboard' | 'reports' | 'workspace' | 'settings'

interface GlobalNavTab {
  id: GlobalTabId
  label: string
  icon: LucideIcon
  href: string
  visible: boolean
}

interface ModuleSlot {
  key: string
  label: string
  /** Accessible name when label is abbreviated (e.g. Add → Add query). */
  ariaLabel?: string
  icon: LucideIcon
  active: boolean
  onClick: () => void
  /** Emphasized primary action (create) — larger center control. */
  primary?: boolean
  /** Soft CSS reminder nudge when inactive. */
  attention?: boolean
}

/** Exact match or `prefix/…` — never a bare-prefix collision with an unrelated route. */
function matchesSection(pathname: string, section: string): boolean {
  return pathname === section || pathname.startsWith(`${section}/`)
}

function resolveActiveGlobalTab(pathname: string): GlobalTabId | null {
  if (pathname === ROUTES.DASHBOARD) return 'dashboard'
  if (pathname === ROUTES.MORE || matchesSection(pathname, ROUTES.WORKSPACE)) return 'workspace'
  if (matchesSection(pathname, ROUTES.REPORTS)) return 'reports'
  if (
    matchesSection(pathname, ROUTES.ACCOUNT) ||
    pathname === ROUTES.SETTINGS ||
    pathname === ROUTES.SETTINGS_SHEETS_SYNC
  )
    return 'settings'
  return null
}

function NavSlot({
  label,
  ariaLabel,
  icon: Icon,
  active,
  onClick,
  reducedMotion,
  layoutId,
  primary = false,
  attention = false,
  dense = false,
}: {
  label: string
  ariaLabel?: string
  icon: LucideIcon
  active: boolean
  onClick: () => void
  reducedMotion: boolean
  layoutId: string
  primary?: boolean
  attention?: boolean
  /** 8+ slots (e.g. Leads with Inbox): one size smaller so "Registered" still fits. */
  dense?: boolean
}) {
  const tween = reducedMotion
    ? { duration: 0 }
    : { duration: 0.16, ease: [0.4, 0, 0.2, 1] as const }
  const nudge = attention && !active && !primary && !reducedMotion

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative flex h-full min-w-0 flex-1 flex-col items-center justify-center outline-none',
        primary && 'z-20 overflow-visible',
      )}
      aria-label={ariaLabel ?? label}
      aria-current={active ? 'page' : undefined}
    >
      {active && !primary ? (
        <motion.span
          layoutId={layoutId}
          className="absolute inset-x-0 top-0 mx-auto h-[2.5px] w-6 rounded-full bg-nav"
          transition={tween}
        />
      ) : null}

      {/* Fixed icon well — primary create is intentionally larger (app-style center CTA). */}
      <motion.span
        whileTap={reducedMotion ? undefined : { scale: primary ? 0.92 : 0.94 }}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full transition-colors duration-150',
          'group-focus-visible:ring-2 group-focus-visible:ring-nav/45 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background',
          primary
            ? cn(
                'btn-cta relative -mt-4 h-12 w-12 text-white shadow-[0_6px_16px_-4px_rgb(15_23_42_/_0.35)]',
                active && 'ring-2 ring-primary/30 ring-offset-2 ring-offset-background',
              )
            : cn(
                'h-8 w-8',
                active
                  ? 'bg-nav/12 text-nav'
                  : 'text-muted-foreground/80 group-hover:bg-muted/50',
                nudge && 'animate-pmms-attention-nudge',
              ),
        )}
      >
        <Icon
          className={cn('shrink-0', primary ? 'h-6 w-6' : 'h-5 w-5')}
          strokeWidth={primary ? 2.25 : 2}
          absoluteStrokeWidth
        />
      </motion.span>

      {/* Label — full words (Registered / Follow up); scale type so 5 slots stay neat. */}
      <span
        className={cn(
          'mt-0.5 flex h-4 w-full max-w-full items-center justify-center px-0',
          dense ? 'text-[9px] leading-none tracking-tight' : 'text-[9px] leading-none tracking-tight min-[390px]:text-[10px]',
          primary
            ? 'font-semibold text-primary'
            : active
              ? 'font-semibold text-nav'
              : 'font-medium text-muted-foreground/80',
        )}
      >
        <span className="block w-full truncate text-center">{label}</span>
      </span>
    </button>
  )
}

export function MobileBottomNav() {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { user, hasPermission } = useAuth()
  const reducedMotion = usePrefersReducedMotion()
  const isSuper = user?.role === 'super_admin'

  const moduleConfig = useMemo(() => resolveMobileModuleNav(pathname), [pathname])

  const moduleTabs = useMemo(() => {
    if (!moduleConfig) return [] as MobileModuleNavTab[]
    return moduleConfig.tabs.filter((tab) => {
      if (moduleConfig.id === 'tasks' && tab.id === 'assigned') {
        return (
          isSuper ||
          hasPermission('tasks.assign') ||
          hasPermission('tasks.view_all')
        )
      }
      return !tab.permission || isSuper || hasPermission(tab.permission)
    })
  }, [moduleConfig, isSuper, hasPermission])

  const onOldCustomers =
    moduleConfig?.id === 'sales-leads' &&
    (new URLSearchParams(search).get('tab') === 'old' || pathname.startsWith(`${ROUTES.SALES_LEADS}/old`))
  const canCreate = Boolean(
    moduleConfig?.create &&
      !onOldCustomers &&
      (isSuper || hasPermission(moduleConfig.create.permission)),
  )

  const createActive = Boolean(
    moduleConfig?.create &&
      (pathname === moduleConfig.create.href ||
        pathname.startsWith(`${moduleConfig.create.href}/`)),
  )

  const globalTabs: GlobalNavTab[] = useMemo(() => {
    const canDashboard = isSuper || hasPermission('dashboard.view')
    const canReports =
      isSuper ||
      hasPermission('dispatch_reports.view') ||
      hasPermission('purchase_reports.view') ||
      hasPermission('reports.view')

    const all: GlobalNavTab[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        href: ROUTES.DASHBOARD,
        visible: canDashboard,
      },
      {
        id: 'reports',
        label: 'Reports',
        icon: BarChart3,
        href: ROUTES.REPORTS,
        visible: canReports,
      },
      {
        id: 'workspace',
        label: 'Workspace',
        icon: Blocks,
        href: ROUTES.WORKSPACE,
        visible: true,
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        href: ROUTES.ACCOUNT,
        visible: true,
      },
    ]

    return all.filter((tab) => tab.visible)
  }, [isSuper, hasPermission])

  const activeGlobal = resolveActiveGlobalTab(pathname)
  const inModule = Boolean(moduleConfig && moduleTabs.length > 0)
  const modeKey = inModule ? `module:${moduleConfig!.id}` : 'global'

  // Replace, not push — a bottom-nav tap is "switch section," not a
  // drill-down, so it must not grow the back-button history stack (see
  // WorkspacePage's goToModule for the same reasoning).
  const go = (href: string) => {
    navigate(href, { replace: true })
  }

  // The create CTA is a real drill-down (opens a form) — unlike every other
  // bottom-nav slot it must push, so the back button returns to the list
  // it was opened from instead of skipping past it.
  const goCreate = (href: string) => {
    navigate(href)
  }

  /** Keep list filters when switching in-module tabs (e.g. Queries ↔ Registered,
   * Received ↔ Assigned). Only the tab-identifying param each module's hrefs
   * carry (`tab` for Leads, `scope` for Tasks) is swapped in — every other
   * filter already in the URL stays untouched. */
  const goModuleTab = (href: string) => {
    const [path, query = ''] = href.split('?')
    const pathBase = path || href
    const incoming = new URLSearchParams(query)
    const incomingTab = incoming.get('tab')
    const currentTab = new URLSearchParams(search).get('tab')
    // Old customers and Inbox are their own pages — never carry Queries filters in or out.
    const ownPage = (tab: string | null) => tab === 'old' || tab === 'inbox'
    const oldBoundary =
      ownPage(incomingTab) ||
      ownPage(currentTab) ||
      pathname.startsWith(`${ROUTES.SALES_LEADS}/old`) ||
      pathname.startsWith(`${ROUTES.SALES_LEADS}/inbox`)
    // Old customers must not keep Queries filters, and leaving it must not
    // push its search/status into Queries (that overwrote the saved list).
    if (
      pathname === pathBase ||
      pathname.startsWith(`${ROUTES.SALES_LEADS}/old`) ||
      pathname.startsWith(`${ROUTES.SALES_LEADS}/inbox`)
    ) {
      if (oldBoundary && pathBase === ROUTES.SALES_LEADS) {
        if (ownPage(incomingTab)) {
          navigate(`${ROUTES.SALES_LEADS}?tab=${incomingTab}`, { replace: true })
          return
        }
        if (incomingTab === 'lead' || incomingTab === 'registered' || incomingTab === 'follow') {
          navigate(salesLeadsListHref({ tab: incomingTab as LeadListTab }), { replace: true })
          return
        }
        navigate(href, { replace: true })
        return
      }
    }
    if (pathname === pathBase) {
      const next = new URLSearchParams(search)
      if (incoming.has('tab')) next.set('tab', incoming.get('tab')!)
      else next.delete('tab')
      if (incoming.has('scope')) next.set('scope', incoming.get('scope')!)
      else next.delete('scope')
      // Stage switches leave reminder deep-links behind (Tasks inbox pin).
      next.delete('reminder')
      next.set('page', '1')
      const qs = next.toString()
      navigate({ pathname: pathBase, search: qs ? `?${qs}` : '' }, { replace: true })
      return
    }
    // Switching to a different module's own tabs — same "switch section"
    // rule as go() above, not a drill-down.
    navigate(href, { replace: true })
  }

  const moduleSlots: ModuleSlot[] = (() => {
    if (!moduleConfig || !inModule) return []
    const slots: ModuleSlot[] = moduleTabs.map((tab) => ({
      key: tab.id,
      label: tab.shortLabel ?? tab.label,
      ariaLabel: tab.label,
      icon: tab.icon,
      active: !createActive && isMobileModuleNavTabActive(tab, pathname, search),
      attention: Boolean(tab.attention),
      onClick: () => goModuleTab(tab.href),
    }))

    if (canCreate && moduleConfig.create) {
      // Center the create action between module tabs when possible.
      // With Queries / Registered / Follow up → insert yields … | Add | Follow up | Exit.
      const insertAt = Math.min(Math.ceil(slots.length / 2), slots.length)
      slots.splice(insertAt, 0, {
        key: 'create',
        label: moduleConfig.create.shortLabel ?? 'Add',
        ariaLabel: moduleConfig.create.label,
        icon: Plus,
        active: createActive,
        primary: true,
        onClick: () => goCreate(moduleConfig.create!.href),
      })
    }

    slots.push({
      key: MOBILE_MODULE_EXIT.id,
      label: MOBILE_MODULE_EXIT.label,
      icon: MOBILE_MODULE_EXIT.icon,
      active: false,
      onClick: () => go(MOBILE_MODULE_EXIT.href),
    })

    return slots
  })()

  return (
    <nav
      aria-label={inModule ? 'Module' : 'Primary'}
      className={cn(
        // `ns-bottom`: smoked-glass skin from nav-shell.css (matches the phone top bar).
        'ns-bottom relative z-40 shrink-0 pb-safe-bottom',
        // `.glass-header` sets overflow:hidden — force visible so the raised Add circle is not clipped.
        canCreate && inModule ? '!overflow-visible' : 'overflow-hidden',
        'glass-header border-t border-border/45',
        'shadow-[0_-1px_12px_rgba(15,23,42,0.04)] dark:shadow-[0_-1px_12px_rgba(0,0,0,0.28)]',
      )}
    >
      {/* Instant swap between global ↔ module bars. The old fade-out-then-in
          (AnimatePresence mode="wait") left the bar empty ~240ms on every
          module enter/exit — the bottom-nav blink on phones. */}
        <div
          key={modeKey}
          className={cn(
            'relative z-[1] grid items-stretch overflow-visible px-1',
            canCreate && inModule ? 'h-[3.75rem]' : 'h-[3.5rem]',
          )}
          style={{
            gridTemplateColumns: `repeat(${inModule ? moduleSlots.length || 1 : globalTabs.length || 1}, minmax(0, 1fr))`,
          }}
        >
          {inModule
            ? moduleSlots.map((slot) => (
                <NavSlot
                  key={slot.key}
                  label={slot.label}
                  ariaLabel={slot.ariaLabel}
                  icon={slot.icon}
                  active={slot.active}
                  primary={slot.primary}
                  attention={slot.attention}
                  dense={moduleSlots.length >= 8}
                  onClick={slot.onClick}
                  reducedMotion={reducedMotion}
                  layoutId={`${modeKey}-indicator`}
                />
              ))
            : globalTabs.map((tab) => (
                <NavSlot
                  key={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  active={tab.id === activeGlobal}
                  onClick={() => go(tab.href)}
                  reducedMotion={reducedMotion}
                  layoutId="bottom-nav-indicator"
                />
              ))}
        </div>
    </nav>
  )
}
